import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Notifications.css';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { Link } from 'react-router-dom';
import { FaHeart, FaComment, FaRetweet, FaUserPlus, FaAt, FaReply, FaExclamationTriangle, FaExclamationCircle, FaStar, FaCheckCircle, FaCalendarCheck } from 'react-icons/fa';
import { CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import 'moment/locale/ja';  // 日本語ロケールを追加
import { useNavigate } from 'react-router-dom';
import { apiService } from '../utils/api';
import config from '../config';
import FavoriteIcon from '@mui/icons-material/Favorite';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MentionIcon from '@mui/icons-material/AlternateEmail';

// 日本時間と日本語ロケールの設定
moment.tz.setDefault('Asia/Tokyo');
moment.locale('ja');

// 画像のURLを正規化するヘルパー関数
const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  // すでに完全なURLの場合はそのまま返す
  if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  
  // "/static/"で始まる相対パスの場合、APIのベースURLと結合
  if (imageUrl.startsWith('/static/')) {
    return `${config.API_BASE_URL}${imageUrl}`;
  }
  
  // /からはじまる場合もAPIのベースURLと結合
  if (imageUrl.startsWith('/')) {
    return `${config.API_BASE_URL}${imageUrl}`;
  }
  
  // それ以外の場合はそのまま返す
  return imageUrl;
};

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, logout } = useAuth();
  const notificationContext = useNotification();
  const { 
    updateNotificationCount, 
    recentNotifications, 
    isPollingActive, 
    setPollingActive 
  } = notificationContext || {};
  const navigate = useNavigate();
  const [markingAsRead, setMarkingAsRead] = useState(false);
  // 通知ページを開いた時点で未読だった通知IDのスナップショット。
  // サーバー側はページを開いた時点で全既読化する（バッジ即消し＆ポーリングでの復活防止）が、
  // 通知一覧の未読背景色はこのスナップショットで判定するため、
  // 別ページに遷移するまで（ページ滞在中）は未読ハイライトが維持される。
  const [initiallyUnreadIds, setInitiallyUnreadIds] = useState(() => new Set());
  // スナップショットを一度だけ確定させるためのフラグ
  const snapshotTakenRef = React.useRef(false);

  // recentNotificationsが更新されたらローカルの通知状態を更新
  useEffect(() => {
    if (recentNotifications && recentNotifications.length > 0) {
      // 新しい通知が来た場合、現在の通知リストと結合して重複を排除
      setNotifications(prevNotifications => {
        // 既存のIDを取得
        const existingIds = new Set(prevNotifications.map(n => n.id));
        
        // 重複しない新しい通知を追加
        const newNotifications = recentNotifications.filter(n => !existingIds.has(n.id));
        
        // 既存の通知に新しい通知を追加し、IDでソート
        return [...prevNotifications, ...newNotifications]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      });
      
      // ローディング状態を解除
      if (loading) {
        setLoading(false);
      }
    }
  }, [recentNotifications, loading]);

  // 通知の取得関数
  const fetchNotifications = async () => {
    if (!token) {
      setError('認証情報が見つかりません。再ログインしてください。');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 [NOTIFICATIONS] Making API request to /api/notifications');
      console.log('🔍 [NOTIFICATIONS] useAuth() token available:', !!token);
      console.log('🔍 [NOTIFICATIONS] useAuth() token preview:', token ? token.substring(0, 20) + '...' : 'none');
      
      // localStorage から直接トークンを確認
      const localStorageToken = localStorage.getItem('authToken');
      console.log('🔍 [NOTIFICATIONS] localStorage authToken available:', !!localStorageToken);
      console.log('🔍 [NOTIFICATIONS] localStorage authToken preview:', localStorageToken ? localStorageToken.substring(0, 20) + '...' : 'none');
      console.log('🔍 [NOTIFICATIONS] Token match:', token === localStorageToken);
      
      // まず簡単なテストエンドポイントを試す
      console.log('🧪 [NOTIFICATIONS] Testing simple endpoint first...');
      try {
        const testResponse = await apiService.get('/api/notifications/test-simple-notifications');
        console.log('✅ [NOTIFICATIONS] Simple test endpoint successful:', testResponse.data);
      } catch (testError) {
        console.log('❌ [NOTIFICATIONS] Simple test endpoint failed:', testError);
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // タイムアウトを10秒に延長
      
      // Authorizationヘッダーを手動で設定しない（apiServiceのインターセプターに任せる）
      const response = await apiService.get('/api/notifications', {
        params: {
          _t: Date.now() // キャッシュ回避のためのタイムスタンプ
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // apiService.get はレスポンスボディを直接返す（response.data ではない）
      const respData = response?.data || response || {};
      console.log('✅ [NOTIFICATIONS] Response data:', respData);
      console.log('✅ [NOTIFICATIONS] Notifications length:', respData?.notifications?.length);

      // グローバル変数として保存（デバッグ用）
      window.lastNotificationResponse = respData;

      if (respData && respData.notifications) {
        // メタ通知（表示メッセージがnullのもの）をフィルタリング
        const validNotifications = respData.notifications.filter(notification => {
          const message = getNotificationMessage(notification);
          return message !== null;
        });
        
        console.log('フィルタリング前の通知数:', respData.notifications.length);
        console.log('フィルタリング後の有効な通知数:', validNotifications.length);
        setNotifications(validNotifications);

        // 初回取得時のみ、未読だった通知IDのスナップショットを確定する。
        // 以降の再取得やポーリングでは上書きしない（滞在中はハイライトを固定）。
        if (!snapshotTakenRef.current) {
          snapshotTakenRef.current = true;
          const unreadIds = new Set(
            validNotifications.filter(n => !n.read && n.id != null).map(n => n.id)
          );
          setInitiallyUnreadIds(unreadIds);
        }
        
        setError(null); // エラーをクリア
        
        // 未読通知数をカウント（有効な通知のみ）
        const unreadCount = validNotifications.filter(notification => !notification.read).length;
        
        // グローバル通知カウントを更新
        updateNotificationCount(unreadCount);
      } else {
        console.error('Invalid response format:', respData);
        setError('通知データの形式が正しくありません。APIから再取得してください。');
      }
    } catch (error) {
      console.error('❌ [NOTIFICATIONS] API error:', error);
      console.error('❌ [NOTIFICATIONS] Error response:', error.response);
      
      if (error.response) {
        // サーバーからのレスポンスがあるエラー
        console.error('❌ [NOTIFICATIONS] Server error:', error.response.status, error.response.data);
        
        if (error.response.status === 401) {
          setError('認証がタイムアウトしました。再ログインしてください。');
          logout(); // 認証切れの場合はログアウト
        } else if (error.response.status === 500) {
          setError(`サーバー内部エラー: ${error.response.data?.error || error.response.statusText}`);
        } else {
          setError(`サーバーエラー: ${error.response.data?.message || error.response.statusText}`);
        }
      } else if (error.request) {
        // リクエストは送信されたがレスポンスがない
        console.error('❌ [NOTIFICATIONS] No response from server:', error.request);
        setError('サーバーが応答していません。ネットワーク接続を確認してください。');
      } else {
        // リクエスト設定中にエラーが発生
        console.error('❌ [NOTIFICATIONS] Request setup error:', error.message);
        setError(`エラーが発生しました: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // コンポーネントのマウント時に通知を取得
  useEffect(() => {
    // 注意: 以前はここで Service Worker を unregister していたが、
    // それが FCM のバックグラウンド通知用 SW (firebase-messaging-sw.js) を巻き添えにし、
    // 通知ページを一度開くとスマホのプッシュ通知が届かなくなる原因になっていた。
    // SSE は廃止済みで SW を無効化する必要はないため、この処理は削除した。

    // ロングポーリングを有効化
    if (typeof setPollingActive === 'function') {
    setPollingActive(true);
    }
    
    // 初期データを取得
    fetchNotifications();

    // 通知ページを開いたらサイドバーバッジ（未読カウント）を消す。
    // サーバー側も即座に全既読化する。これにより、ページ滞在中に走る
    // グローバルの未読カウントポーリングでバッジが復活するのを防ぐ。
    // ※通知一覧の未読背景色は fetchNotifications 時のスナップショット
    //   （initiallyUnreadIds）で判定するため、サーバーを既読化しても
    //   別ページに遷移するまで未読ハイライトは維持される。
    if (typeof updateNotificationCount === 'function') {
      updateNotificationCount(0);
    }
    apiService.post('/api/notifications/mark-all-read', {}).catch(() => {});
    
    // アンマウント時に実行されるクリーンアップ関数
    return () => {
      // ロングポーリングを無効化（Service Worker はもう触らない）
      if (typeof setPollingActive === 'function') {
      setPollingActive(false);
      }
    };
  }, []);

  // すべての通知を既読にする
  const markAllAsRead = async () => {
    if (markingAsRead || notifications.length === 0) return;
    
    setMarkingAsRead(true);
    
    try {
      const response = await apiService.post('/api/notifications/mark-all-read', {});
      
      if (response.status === 200) {
        // 通知の既読状態を更新
        setNotifications(prev => prev.map(notification => ({
          ...notification,
          read: true
        })));

        // 「すべて既読にする」を押したときは未読ハイライトも即座に消す
        setInitiallyUnreadIds(new Set());
        
        // 未読カウントをゼロに更新
        updateNotificationCount(0);
      }
    } catch (error) {
      console.error('❌ [NOTIFICATIONS] Mark all as read error:', error);
      setError('通知の既読化に失敗しました。再試行してください。');
    } finally {
      setMarkingAsRead(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <FaHeart className="notification__icon notification__icon--like" />;
      case 'comment':
        return <FaComment className="notification__icon notification__icon--comment" />;
      case 'follow':
        return <FaUserPlus className="notification__icon notification__icon--follow" />;
      case 'retweet':
        return <FaRetweet className="notification__icon notification__icon--retweet" />;
      case 'mention':
        return <FaAt className="notification__icon notification__icon--mention" />;
      case 'reply':
        return <FaReply className="notification__icon notification__icon--reply" />;
      case 'comment_like':
        return <FaHeart className="notification__icon notification__icon--comment-like" />;
      case 'comment_reply':
        return <FaComment className="notification__icon notification__icon--comment-reply" />;
      case 'report_reply':
        return <FaComment className="notification__icon notification__icon--report-reply" />;
      case 'review':
        return <FaStar className="notification__icon notification__icon--review" />;
      case 'review_approved':
        return <FaCheckCircle className="notification__icon notification__icon--review-approved" />;
      case 'review_reply':
        return <FaReply className="notification__icon notification__icon--review-reply" />;
      case 'reservation_request':
        return <FaCalendarCheck className="notification__icon notification__icon--reservation" />;
      case 'reservation_status':
        return <FaCalendarCheck className="notification__icon notification__icon--reservation" />;
      case 'reservation_reminder':
        return <FaCalendarCheck className="notification__icon notification__icon--reservation" />;
      case 'message':
        return <FaComment className="notification__icon notification__icon--comment" />;
      case 'ad_budget_depleted':
        return <FaExclamationTriangle className="notification__icon notification__icon--warning" />;
      case 'ad_budget_low':
        return <FaExclamationCircle className="notification__icon notification__icon--warning" />;
      default:
        return null;
    }
  };

  const getNotificationMessage = (notification) => {
    try {
      if (!notification) {
        console.error('notification が null または undefined です');
        return <>通知情報が取得できませんでした</>;
      }
      
    const { type, sender } = notification;
    const displayName = sender ? (sender.display_name || sender.username) : '不明なユーザー';

    switch (type) {
      case 'like':
        return <><span className="notification__username">{displayName}</span>があなたの投稿にいいね<span className="notification__action notification__action--like">♥</span>しました</>;
      case 'comment':
        return <><span className="notification__username">{displayName}</span>があなたの投稿にコメント<span className="notification__action notification__action--comment">💬</span>しました</>;
      case 'follow':
        return <><span className="notification__username">{displayName}</span>があなたをフォロー<span className="notification__action notification__action--follow">👤</span>しました</>;
      case 'retweet':
        return <><span className="notification__username">{displayName}</span>があなたの投稿をリツイート<span className="notification__action notification__action--retweet">🔄</span>しました</>;
      case 'mention':
        return <><span className="notification__username">{displayName}</span>があなたにメンション<span className="notification__action notification__action--mention">@</span>しました</>;
      case 'reply':
        return <><span className="notification__username">{displayName}</span>があなたの投稿に返信<span className="notification__action notification__action--reply">↩</span>しました</>;
      case 'comment_like':
        return <><span className="notification__username">{displayName}</span>があなたのコメントにいいね<span className="notification__action notification__action--like">♥</span>しました</>;
      case 'comment_reply':
        return <><span className="notification__username">{displayName}</span>があなたのコメントに返信<span className="notification__action notification__action--reply">↩</span>しました</>;
      case 'report_reply':
        return <><span className="notification__username">{displayName}</span>からあなたの通報に対する返答<span className="notification__action notification__action--report-reply">📢</span>があります</>;
      case 'review':
        return <><span className="notification__username">{displayName}</span>があなたにクチコミを投稿<span className="notification__action notification__action--review">⭐</span>しました（承認待ち）</>;
      case 'review_approved':
        return <>あなたのクチコミが承認<span className="notification__action notification__action--review-approved">✅</span>されました</>;
      case 'review_reply':
        return <><span className="notification__username">{displayName}</span>があなたのクチコミに返信<span className="notification__action notification__action--review-reply">↩</span>しました</>;
      case 'reservation_request':
        return <><span className="notification__username">{displayName}</span>から仮予約リクエスト<span className="notification__action notification__action--reservation">📅</span>が届きました{notification.message ? `：${notification.message}` : ''}</>;
      case 'reservation_status':
        return <>仮予約のステータスが更新されました<span className="notification__action notification__action--reservation">📅</span>{notification.message ? `：${notification.message}` : ''}</>;
      case 'reservation_reminder':
        return <>予約リマインド<span className="notification__action notification__action--reservation">⏰</span>{notification.message ? `：${notification.message}` : ''}</>;
      case 'message':
        return null; // メッセージはサイドバーバッジで表示するため通知一覧には含めない
      case 'ad_budget_depleted':
        return <><span className="notification__username">{displayName}</span>からの通知: 広告予算が消費されました<span className="notification__action notification__action--warning">⚠️</span></>;
      case 'ad_budget_low':
        const remainingClicks = notification.remaining_clicks || '数';
        return <><span className="notification__username">{displayName}</span>からの通知: 広告予算が残り{remainingClicks}クリック分です<span className="notification__action notification__action--warning">⚠️</span></>;
      default:
        // メタ通知を無効化：「からの通知」は不要なので空の状態を返す
        console.warn('Unknown notification type:', type, notification);
        return null; // この通知は表示しない
    }
    } catch (error) {
      console.error('getNotificationMessage エラー:', error);
      return <>通知メッセージの読み込みに失敗しました</>;
    }
  };

  const formatNotificationTime = (timestamp) => {
    try {
      if (!timestamp) {
        console.warn('タイムスタンプが空です');
        return '現在';
      }
      
      // タイムスタンプがStringでない場合の対応
      let dateStr = timestamp;
      if (typeof timestamp === 'object' && timestamp.toISOString) {
        dateStr = timestamp.toISOString();
      } else if (typeof timestamp !== 'string') {
        console.warn('タイムスタンプの型が不正です:', typeof timestamp, timestamp);
        return '不明な日時';
      }
      
      // momentが正しく定義されているかチェック
      if (typeof moment !== 'function') {
        console.error('moment is not a function:', moment);
        return '日時計算エラー';
      }
      
      // momentを使って明示的に日本時間に変換
      const date = moment.utc(dateStr);
      if (!date.isValid()) {
        console.warn('無効な日付です:', dateStr);
        return '不明な日時';
      }
      
      const now = moment();
      const diffSec = now.diff(date, 'seconds');
      const diffMin = now.diff(date, 'minutes');
      const diffHour = now.diff(date, 'hours');
      const diffDay = now.diff(date, 'days');
      
      if (diffSec < 0) {
        return 'たった今';
      } else if (diffSec < 60) {
        return `${diffSec}秒前`;
      } else if (diffMin < 60) {
        return `${diffMin}分前`;
      } else if (diffHour < 24) {
        return `${diffHour}時間前`;
      } else if (diffDay < 30) {
        return `${diffDay}日前`;
      } else {
        return date.format('YYYY年M月D日 HH:mm');
      }
    } catch (e) {
      console.error('formatNotificationTime エラー:', e);
      return '不明な日時';
    }
  };

  const getNotificationLink = (notification) => {
    const { type, post_id, sender } = notification;

    switch (type) {
      case 'like':
      case 'comment':
      case 'retweet':
      case 'reply':
      case 'mention':
        if (!notification.post) {
          return '#';
        }
        return `/post/${notification.post.id}`;
      case 'follow':
        return sender ? `/profile/${sender.username}` : '#';
      case 'comment_like':
      case 'comment_reply':
        // コメント関連の通知も投稿ページに遷移（対象コメントへスクロールする機能は今後実装）
        if (!notification.post) {
          return '#';
        }
        return `/post/${notification.post.id}`;
      case 'report_reply':
        return '#'; // 通報返信はリンク先なし
      case 'review':
      case 'review_approved':
      case 'review_reply':
        // クチコミ詳細ページへ（target_id があれば）
        if (notification.target_id) {
          return `/reviews-ranking/${notification.target_id}`;
        }
        return sender ? `/profile/${sender.username}` : '#';
      case 'reservation_request':
      case 'reservation_status':
      case 'reservation_reminder':
        // 仮予約詳細ページへ
        return notification.reservation_id ? `/reservations/${notification.reservation_id}` : '#';
      case 'message':
        return '/messages'; // メッセージページへ
      case 'ad_budget_depleted':
      case 'ad_budget_low':
        return '/corporate/ads'; // 広告管理ページへ
      default:
        return '#';
    }
  };

  const loadDummyNotifications = () => {
    setLoading(true);
    
    const dummyNotifications = [
      {
        id: 1,
        type: 'like',
        read: false,
        created_at: new Date(Date.now() - 5 * 60000).toISOString(),
        sender: {
          id: 2,
          username: 'yamada_taro',
          display_name: '山田太郎',
          avatar: 'https://picsum.photos/id/1001/200/200',
          verified: false
        },
        post: {
          id: 101,
          text: 'こんにちは！SNSアプリのデモ投稿です。いいねやコメントなどの機能をお試しください。#デモ #SNS',
          image: 'https://picsum.photos/id/1018/300/200'
        }
      },
      {
        id: 2,
        type: 'comment',
        read: true,
        created_at: new Date(Date.now() - 2 * 3600000).toISOString(),
        sender: {
          id: 3,
          username: 'tanaka_hanako',
          display_name: '田中花子',
          avatar: 'https://picsum.photos/id/1005/200/200',
          verified: true
        },
        post: {
          id: 102,
          text: '今日は天気がいいですね！公園でランチを楽しんでいます。#ランチ #公園',
          image: null
        },
        comment: {
          id: 201,
          text: 'とても素敵な一日ですね！私も外出しようと思います。'
        }
      },
      {
        id: 3,
        type: 'follow',
        read: false,
        created_at: new Date(Date.now() - 24 * 3600000).toISOString(),
        sender: {
          id: 4,
          username: 'suzuki_ichiro',
          display_name: '鈴木一郎',
          avatar: 'https://picsum.photos/id/1027/200/200',
          verified: false
        }
      },
      {
        id: 4,
        type: 'retweet',
        read: true,
        created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString(),
        sender: {
          id: 5,
          username: 'sato_yuki',
          display_name: '佐藤ゆき',
          avatar: 'https://picsum.photos/id/1012/200/200',
          verified: false
        },
        post: {
          id: 103,
          text: '新商品のレビュー記事を公開しました！詳しくはプロフィールのリンクから。 #新商品 #レビュー',
          image: 'https://picsum.photos/id/1019/300/200'
        }
      },
      {
        id: 5,
        type: 'mention',
        read: false,
        created_at: new Date(Date.now() - 12 * 3600000).toISOString(),
        sender: {
          id: 6,
          username: 'kato_masaki',
          display_name: '加藤正樹',
          avatar: 'https://picsum.photos/id/1074/200/200',
          verified: true
        },
        post: {
          id: 104,
          text: 'みなさん、新しいカフェをご存知ですか？ @admin_user おすすめのスポットを教えてください！ #カフェ #おすすめ',
          image: 'https://picsum.photos/id/431/300/200'
        }
      }
    ];
    
    
    
    setTimeout(() => {
      setNotifications(dummyNotifications);
      setLoading(false);
      setError(null);
    }, 500);
  };

  return (
    <div className="notifications">
      <div className="notifications__header">
        <h2>通知</h2>
        {notifications.length > 0 && (
          <button 
            className="notifications__markAllAsRead"
            onClick={markAllAsRead}
          >
            すべて既読にする
          </button>
        )}
      </div>

      {loading ? (
        <div className="notifications__loading">
          <CircularProgress size={40} />
          <p>通知を読み込み中...</p>
        </div>
      ) : error ? (
        <div className="notifications__error">
          <p>{error}</p>
          <div className="notifications__buttons">
            <button onClick={fetchNotifications} className="notifications__retryButton">
              APIから再取得
            </button>
            <button onClick={loadDummyNotifications} className="notifications__demoButton">
              デモデータを表示
            </button>
          </div>
        </div>
      ) :       notifications.length === 0 ? (
        <div className="notifications__empty">
          <p>通知はありません</p>
        </div>
      ) : (
        <div className="notifications__list">
          {notifications
            .filter(notification => {
              // メタ通知（メッセージがnull）を除外
              const message = getNotificationMessage(notification);
              return message !== null;
            })
            .map((notification, index) => {
            // 一意のキーを生成（通知にIDがない場合はindexとタイプと作成日時の組み合わせを使用）
            const uniqueKey = notification.id || `${index}-${notification.type}-${notification.created_at || new Date().getTime()}`;

            // 未読ハイライトの判定:
            // ページを開いた時点で未読だった通知（スナップショット）で、
            // かつローカルでまだ既読化していないものだけを未読表示する。
            // これにより、サーバー側を全既読化しても滞在中はハイライトが残り、
            // 個別クリックしたものだけ即座に既読背景へ変わる。
            const isUnread = initiallyUnreadIds.has(notification.id) && !notification.read;
            
            return (
              <Link 
                to={getNotificationLink(notification)} 
                key={uniqueKey}
                className={`notification ${isUnread ? 'notification--unread' : 'notification--read'}`}
                onClick={(e) => {
                  // クリックした通知を既読にして背景色を変更
                  if (!notification.read) {
                    setNotifications((prev) =>
                      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
                    );
                    if (notification.id) {
                      apiService.put(`/api/notifications/${notification.id}/read`, {}).catch(() => {});
                    }
                  }
                  // 投稿が存在しない場合は通常のリンク遷移を防止
                  if (!notification.post && ['like', 'comment', 'retweet', 'reply', 'mention'].includes(notification.type)) {
                    e.preventDefault();
                    alert('この投稿は削除されました');
                  }
                  // report_reply はリンクなしのためデフォルト動作を防止
                  if (notification.type === 'report_reply' || notification.type === 'ad_budget_depleted' || notification.type === 'ad_budget_low') {
                    if (getNotificationLink(notification) === '#') {
                      e.preventDefault();
                    }
                  }
                }}
              >
                <div className="notification__iconContainer">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification__content">
                  <div className="notification__avatar">
                    <img 
                      src={normalizeImageUrl(notification.sender?.avatar) || '/images/default-avatar.png'} 
                      alt={notification.sender?.display_name || 'ユーザー'} 
                    />
                  </div>
                  <div className="notification__details">
                    <p className="notification__message">
                      {getNotificationMessage(notification)}
                    </p>
                    
                    {notification.post ? (
                      <div className="notification__postPreview">
                        <p>{notification.post.text}</p>
                        {notification.post.image && (
                          <div className="notification__postImage">
                            <img src={normalizeImageUrl(notification.post.image)} alt="投稿画像" />
                          </div>
                        )}
                      </div>
                    ) : ['like', 'comment', 'retweet', 'reply', 'mention'].includes(notification.type) ? (
                      <div className="notification__postPreview notification__postPreview--deleted">
                        <p><i>この投稿は削除されました</i></p>
                      </div>
                    ) : null}
                    
                    {notification.comment && (
                      <div className="notification__commentPreview">
                        <p>{notification.comment.text}</p>
                      </div>
                    )}
                    
                    {notification.type === 'report_reply' && notification.message && (
                      <div className="notification__reportReply" style={{ backgroundColor: '#f0f7ff', padding: '8px 12px', borderRadius: '4px', marginTop: '4px', fontSize: '0.9em' }}>
                        <p style={{ margin: 0 }}>{notification.message}</p>
                      </div>
                    )}
                    
                    {notification.report && notification.report.admin_comment && !notification.message && (
                      <div className="notification__reportReply" style={{ backgroundColor: '#f0f7ff', padding: '8px 12px', borderRadius: '4px', marginTop: '4px', fontSize: '0.9em' }}>
                        <p style={{ margin: 0 }}>{notification.report.admin_comment}</p>
                      </div>
                    )}
                    
                    <span className="notification__time">
                      {formatNotificationTime(notification.created_at)}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Notifications; 